import { useState } from "react";
import { useLocalStorage } from "usehooks-ts";
import {
	ChevronsDownUp,
	ChevronsUpDown,
	ListTodo,
	Loader2,
	Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PermissionGate } from "@/components/PermissionGate";
import { reportError } from "../utils";
import { useChecklist } from "./useChecklist";
import { ChecklistProgressBar } from "./ChecklistProgressBar";
import { ChecklistItemRow } from "./ChecklistItemRow";
import { ChecklistTemplateMenu } from "./ChecklistTemplateMenu";

export function ChecklistSection({
	projectId,
	taskId,
	canEdit,
}: {
	projectId: string;
	taskId: string;
	canEdit: boolean;
}) {
	const {
		items,
		completed,
		total,
		progress,
		isLoading,
		error,
		addItem,
		toggleItem,
		renameItem,
		removeItem,
		applyTemplate,
		refresh,
	} = useChecklist(projectId, taskId);

	const [draft, setDraft] = useState("");
	const [adding, setAdding] = useState(false);
	const [compact, setCompact] = useLocalStorage("checklist-compact", true);

	async function handleAdd() {
		const next = draft.trim();
		if (!next) return;
		setAdding(true);
		try {
			await addItem(next);
			setDraft("");
		} catch (e) {
			reportError(e);
		} finally {
			setAdding(false);
		}
	}

	return (
		<section>
			<div className="flex items-center justify-between mb-3">
				<div className="flex items-center gap-2">
					<ListTodo className="h-4 w-4 text-muted" />
					<h3 className="text-sm font-semibold text-foreground">
						Checklist
					</h3>
					{total > 0 && (
						<span className="text-xs text-muted">({total})</span>
					)}
				</div>
				<div className="flex items-center gap-1.5">
					{total > 0 && (
						<Button
							variant="ghost"
							size="icon-xs"
							onClick={() => setCompact(!compact)}
							aria-label={compact ? "Expand all items" : "Compact items"}
							title={compact ? "Expanded view" : "Compact view"}
						>
							{compact ? (
								<ChevronsUpDown className="h-3.5 w-3.5" />
							) : (
								<ChevronsDownUp className="h-3.5 w-3.5" />
							)}
						</Button>
					)}
					{canEdit && (
						<PermissionGate feature="Create & edit tasks">
							<ChecklistTemplateMenu
								projectId={projectId}
								onApply={applyTemplate}
							/>
						</PermissionGate>
					)}
				</div>
			</div>

			{total > 0 && (
				<ChecklistProgressBar
					progress={progress}
					completed={completed}
					total={total}
				/>
			)}

			{isLoading ? (
				<div className="flex items-center gap-2 text-xs text-muted py-4">
					<Loader2 className="h-3.5 w-3.5 animate-spin" />
					Loading checklist...
				</div>
			) : error ? (
				<div className="rounded-lg border border-dashed border-border px-4 py-6 text-center">
					<p className="text-xs text-destructive">
						Failed to load checklist.
					</p>
				</div>
			) : (
				<div className="rounded-lg border border-border bg-muted-subtle/30 p-3 space-y-1.5">
					{items.length === 0 ? (
						<p className="text-xs text-muted text-center py-2">
							No checklist items yet
						</p>
					) : (
						items.map((item) => (
							<ChecklistItemRow
								key={item.id}
								projectId={projectId}
								taskId={taskId}
								item={item}
								canEdit={canEdit}
								compact={compact}
								onToggle={toggleItem}
								onRename={renameItem}
								onRemove={removeItem}
								onAttachmentsChanged={() => refresh()}
							/>
						))
					)}

					{canEdit && (
						<PermissionGate feature="Create & edit tasks">
							<div className="flex items-center gap-2 pt-1.5">
								<Input
									value={draft}
									disabled={adding}
									placeholder="Add an item..."
									onChange={(e) => setDraft(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === "Enter") {
											e.preventDefault();
											handleAdd();
										}
									}}
									className="h-7 text-sm flex-1"
								/>
								<Button
									variant="outline"
									size="sm"
									onClick={handleAdd}
									disabled={adding || !draft.trim()}
								>
									{adding ? (
										<Loader2 className="h-3 w-3 animate-spin" />
									) : (
										<Plus className="h-3 w-3" />
									)}
								</Button>
							</div>
						</PermissionGate>
					)}
				</div>
			)}
		</section>
	);
}
